<template>
  <div :class="$style.container">
    <!-- 통계 탭 네비게이션 -->
    <StatsTabs />

    <!-- 로딩 상태 -->
    <div v-if="loading">
      <LoadingScreen loading-text="코어 유저 데이터를 분석하는 중..." />
    </div>

    <!-- 에러 상태 -->
    <div v-else-if="error" :class="$style.error">
      <p>{{ error }}</p>
      <button @click="loadCoreUsers" :class="$style.retryButton">다시 시도</button>
    </div>

    <!-- 통계 데이터 -->
    <div v-else>
      <div :class="$style.header">
        <h1 :class="$style.title">🎯 코어 유저 관리</h1>
        <p :class="$style.subtitle">최근 14일 내 1회 이상 방문한 활성 사용자 분석 (디버깅 모드)</p>
      </div>

      <!-- 요약 정보 -->
      <div :class="$style.summary">
        <div :class="$style.summaryCard">
          <h3>코어 유저 수</h3>
          <p :class="$style.summaryNumber">{{ coreUsers.length }}명</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>평균 방문 횟수</h3>
          <p :class="$style.summaryNumber">{{ averageVisits.toFixed(1) }}회</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>총 활동 수</h3>
          <p :class="$style.summaryNumber">{{ totalActivities }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>평균 활동 수</h3>
          <p :class="$style.summaryNumber">{{ averageActivities.toFixed(1) }}건</p>
        </div>
      </div>

      <!-- 코어 유저 목록 -->
      <div :class="$style.userListSection">
        <div v-if="coreUsers.length === 0" :class="$style.emptyState">
          코어 유저가 없습니다.
        </div>
        <div v-else :class="$style.userList">
          <div v-for="(user, index) in coreUsers" :key="user.email" :class="$style.userItem">
            <div :class="$style.userRank">{{ index + 1 }}</div>
            <div :class="$style.userMain">
              <div :class="$style.userHeader">
                <img v-if="user.profileImage" :src="user.profileImage" :alt="user.nickname" :class="$style.profileImage"
                  @error="handleImageError" />
                <div v-else :class="$style.profilePlaceholder">
                  {{ user.email ? user.email[0].toUpperCase() : '?' }}
                </div>
                <div :class="$style.userInfo">
                  <div :class="$style.userNickname">{{ user.nickname || '닉네임 없음' }}</div>
                  <div :class="$style.userEmail">{{ user.email }}</div>
                </div>
              </div>
              <div :class="$style.userStats">
                <div :class="$style.statItem">
                  <span :class="$style.statLabel">방문 횟수</span>
                  <span :class="$style.statValue">{{ user.visitCount }}회</span>
                </div>
                <div :class="$style.statItem">
                  <span :class="$style.statLabel">총 활동</span>
                  <span :class="$style.statValue">{{ user.activityCount }}건</span>
                </div>
                <div :class="$style.statItem">
                  <span :class="$style.statLabel">최근 방문</span>
                  <span :class="$style.statValue">{{ formatDate(user.lastVisit) }}</span>
                </div>
              </div>
              <div :class="$style.activityBreakdown">
                <div v-if="user.liveViews > 0" :class="$style.activityBadge">
                  🎤 라이브 조회 {{ user.liveViews }}
                </div>
                <div v-if="user.artistViews > 0" :class="$style.activityBadge">
                  ⭐ 아티스트 조회 {{ user.artistViews }}
                </div>
                <div v-if="user.likes > 0" :class="$style.activityBadge">
                  ❤️ 좋아요 {{ user.likes }}
                </div>
                <div v-if="user.comments > 0" :class="$style.activityBadge">
                  💬 댓글 {{ user.comments }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCoreUserStats } from "@/utils/read/user";
import StatsTabs from "@/components/StatsTabs.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";

export default {
  name: "CoreUserStats",
  components: {
    StatsTabs,
    LoadingScreen,
  },
  data() {
    return {
      coreUsers: [],
      loading: false,
      error: null,
      days: 14,
      minVisits: 3, // 디버깅용으로 1로 설정
    };
  },
  computed: {
    averageVisits() {
      if (this.coreUsers.length === 0) return 0;
      const total = this.coreUsers.reduce((sum, user) => sum + user.visitCount, 0);
      return total / this.coreUsers.length;
    },
    totalActivities() {
      return this.coreUsers.reduce((sum, user) => sum + user.activityCount, 0);
    },
    averageActivities() {
      if (this.coreUsers.length === 0) return 0;
      return this.totalActivities / this.coreUsers.length;
    },
  },
  async mounted() {
    await this.loadCoreUsers();
  },
  methods: {
    async loadCoreUsers() {
      this.loading = true;
      this.error = null;

      try {
        console.log('🔍 코어 유저 API 호출 시작');
        console.log('📅 조회 기간:', this.days, '일');
        console.log('🎯 최소 방문 횟수:', this.minVisits, '회');

        const coreUsers = await getCoreUserStats(this.days, this.minVisits);

        console.log('✅ API 응답:', coreUsers.length, '명');

        this.coreUsers = coreUsers;
      } catch (error) {
        console.error("❌ 코어 유저 로딩 오류:", error);
        this.error = `코어 유저 데이터를 불러오는 중 오류가 발생했습니다: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 60) return `${minutes}분 전`;
      if (hours < 24) return `${hours}시간 전`;
      if (days < 7) return `${days}일 전`;

      return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
    },
    handleImageError(e) {
      e.target.src = require('@/assets/images/default_profile.png');
    },
  },
};
</script>

<style module>
.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
  background: #f8f9fa;
}

.error {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.error p {
  color: #f44336;
  font-size: 16px;
  margin-bottom: 16px;
}

.retryButton {
  padding: 10px 24px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;
}

.retryButton:hover {
  background: #1976d2;
}

.header {
  margin-bottom: 30px;
  text-align: center;
}

.title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.summaryCard {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 24px;
  border-radius: 16px;
  text-align: center;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: transform 0.2s;
}

.summaryCard:hover {
  transform: translateY(-4px);
}

.summaryCard h3 {
  font-size: 14px;
  margin: 0 0 12px 0;
  opacity: 0.9;
  font-weight: 500;
}

.summaryNumber {
  font-size: 32px;
  font-weight: bold;
  margin: 0;
}

.userListSection {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.emptyState {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}

.userList {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.userItem {
  display: flex;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 16px;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.userItem:hover {
  background: #f0f1f3;
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.userRank {
  font-size: 24px;
  font-weight: bold;
  color: #667eea;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.userMain {
  flex: 1;
  min-width: 0;
}

.userHeader {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.profileImage {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.profilePlaceholder {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.userInfo {
  flex: 1;
  min-width: 0;
}

.userNickname {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.userEmail {
  font-size: 14px;
  color: #666;
}

.userStats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.statItem {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.statLabel {
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.statValue {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.activityBreakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.activityBadge {
  padding: 8px 16px;
  background: white;
  border-radius: 24px;
  font-size: 13px;
  color: #666;
  border: 2px solid #e0e0e0;
  font-weight: 500;
  transition: all 0.2s;
}

.activityBadge:hover {
  border-color: #667eea;
  color: #667eea;
}

/* 반응형 */
@media (max-width: 768px) {
  .container {
    padding: 16px;
  }

  .title {
    font-size: 24px;
  }

  .summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .summaryCard {
    padding: 16px;
  }

  .summaryCard h3 {
    font-size: 12px;
  }

  .summaryNumber {
    font-size: 24px;
  }

  .userListSection {
    padding: 16px;
  }

  .userItem {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .userRank {
    min-width: auto;
    align-self: flex-start;
  }

  .userHeader {
    gap: 12px;
  }

  .profileImage,
  .profilePlaceholder {
    width: 48px;
    height: 48px;
  }

  .profilePlaceholder {
    font-size: 20px;
  }

  .userNickname {
    font-size: 16px;
  }

  .userEmail {
    font-size: 13px;
  }

  .userStats {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px;
  }

  .statItem {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .statValue {
    font-size: 16px;
  }

  .activityBadge {
    font-size: 12px;
    padding: 6px 12px;
  }
}
</style>
